import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ShieldCheck, XCircle, LockKey, ArrowRight, Certificate as CertIcon } from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Verify() {
  const { certNo: rawCertNo } = useParams();
  const certNo = decodeURIComponent(rawCertNo || "");

  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let alive = true;
    axios
      .get(`${BACKEND}/api/verify/certificate/${encodeURIComponent(certNo)}`)
      .then((r) => alive && setState({ loading: false, data: r.data, error: null }))
      .catch((e) => alive && setState({ loading: false, data: null, error: e?.message || "Network error" }));
    return () => {
      alive = false;
    };
  }, [certNo]);

  return (
    <div className="min-h-screen grain-bg flex flex-col">
      {/* Top bar */}
      <header className="border-b border-[var(--vs-border)] bg-[var(--vs-bg)]/85 backdrop-blur-md">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-md bg-[var(--vs-secondary)] text-[var(--vs-bg)] grid place-items-center font-display font-bold">VS</div>
            <div className="leading-tight">
              <div className="font-display font-semibold">Vidya Seva</div>
              <div className="text-[10px] tracking-[0.18em] uppercase text-[var(--vs-text-secondary)]">Public verification</div>
            </div>
          </Link>
          <Link
            to="/verify"
            className="text-xs underline underline-offset-4 hover:text-[var(--vs-secondary)]"
            data-testid="verify-search-link"
          >
            Verify another →
          </Link>
        </div>
      </header>

      <main className="flex-1 max-w-5xl w-full mx-auto px-6 lg:px-8 py-10 lg:py-16">
        <div className="eyebrow mb-3">Certificate verification</div>
        <h1 className="font-display text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.05]">
          {(() => {
            if (state.loading) return <>Verifying…</>;
            if (state.data?.valid) {
              return <>This certificate is <span className="italic font-light text-[var(--vs-secondary)]">authentic</span>.</>;
            }
            return <>We could not verify this certificate.</>;
          })()}
        </h1>
        <p className="mt-3 text-sm text-[var(--vs-text-secondary)] max-w-2xl">
          Reference number{" "}
          <span className="font-mono text-[var(--vs-text)]">{certNo || "—"}</span>
          {state.data?.institute_name ? (
            <> · Issued by <span className="font-mono text-[var(--vs-text)]">{state.data.institute_name}</span></>
          ) : null}
        </p>

        {/* Status card */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mt-8 vs-card p-6 lg:p-8"
          data-testid="verify-status-card"
        >
          {state.loading && (
            <div className="text-sm text-[var(--vs-text-secondary)]">Looking up certificate registry…</div>
          )}

          {!state.loading && state.error && (
            <div className="flex items-start gap-4">
              <XCircle size={28} weight="duotone" className="text-[var(--vs-danger)] mt-0.5" />
              <div>
                <div className="font-display text-xl font-semibold">Verification service unavailable</div>
                <div className="text-sm text-[var(--vs-text-secondary)] mt-1">{state.error}. Please try again in a moment.</div>
              </div>
            </div>
          )}

          {!state.loading && state.data?.valid && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-10" data-testid="verify-valid">
              <div className="lg:col-span-2">
                <div className="flex items-center gap-3 text-[var(--vs-success)]">
                  <ShieldCheck size={32} weight="duotone" />
                  <span className="font-display text-2xl font-semibold">Genuine · Status: VALID</span>
                </div>
                <p className="mt-3 text-sm text-[var(--vs-text-secondary)] max-w-xl leading-relaxed">{state.data.message}</p>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Certificate No." value={state.data.cert_no} mono />
                  <Field label="Type" value={state.data.type_label} />
                  <Field label="Student name" value={state.data.student_name} />
                  <Field label="Student ID (VS-ID)" value={state.data.student_vs_id} mono />
                  <Field label="Reason" value={state.data.reason} />
                  <Field
                    label="Issued on"
                    value={state.data.issued_at ? new Date(state.data.issued_at).toLocaleDateString() : "—"}
                    mono
                  />
                </div>
              </div>

              <div className="lg:col-span-1">
                <div className="rounded-md border border-[var(--vs-border)] bg-[var(--vs-bg-subtle)] p-5">
                  <div className="eyebrow mb-2">For verifying institutions</div>
                  <p className="text-xs text-[var(--vs-text-secondary)] leading-relaxed">
                    This page is publicly accessible. Anyone holding a Vidya Seva certificate can paste its number into the URL and receive an authoritative answer from our registry.
                  </p>
                  <div className="vs-divider my-4" />
                  <div className="flex items-center gap-2 text-[11px] font-mono text-[var(--vs-secondary)]">
                    <LockKey size={12} weight="fill" /> AES-256 encrypted registry
                  </div>
                  <div className="flex items-center gap-2 text-[11px] font-mono text-[var(--vs-secondary)] mt-1.5">
                    <ShieldCheck size={12} weight="fill" /> TLS 1.2+ in transit
                  </div>
                </div>
              </div>
            </div>
          )}

          {!state.loading && state.data && state.data.valid === false && (
            <div className="flex items-start gap-4" data-testid="verify-invalid">
              <XCircle size={28} weight="duotone" className="text-[var(--vs-danger)] mt-0.5" />
              <div>
                <div className="font-display text-xl font-semibold">No matching certificate found</div>
                <p className="text-sm text-[var(--vs-text-secondary)] mt-1 max-w-xl leading-relaxed">
                  {state.data.message}
                </p>
                <div className="mt-4 text-[11px] font-mono text-[var(--vs-text-secondary)]">Reference checked: <span className="text-[var(--vs-text)]">{state.data.cert_no}</span></div>
                <Link
                  to="/verify"
                  className="mt-5 inline-flex items-center gap-2 text-sm underline underline-offset-4 hover:text-[var(--vs-secondary)]"
                >
                  Try another reference number <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          )}
        </motion.div>

        <div className="mt-10 text-xs font-mono text-[var(--vs-text-secondary)]">
          Powered by Vidya Seva Welfare Trust · Public certificate registry · No login required.
        </div>
      </main>

      <footer className="border-t border-[var(--vs-border)] bg-[var(--vs-bg-subtle)]">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-6 flex items-center justify-between">
          <Link to="/" className="text-xs text-[var(--vs-text-secondary)] underline underline-offset-4 hover:text-[var(--vs-secondary)]">← Back to landing</Link>
          <div className="flex items-center gap-2 text-[11px] font-mono text-[var(--vs-secondary)]">
            <CertIcon size={14} weight="fill" /> Public registry · Anyone can verify
          </div>
        </div>
      </footer>
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="eyebrow">{label}</div>
      <div className={`mt-1 ${mono ? "font-mono text-sm" : "font-display text-base font-medium"}`}>{value || "—"}</div>
    </div>
  );
}
